import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useState, useTransition } from "react";
import {
  useLazyLoadQuery,
  useMutation,
  useRefetchableFragment,
} from "react-relay";
import { InternBrandJobsCleanupMutation } from "./__generated__/InternBrandJobsCleanupMutation.graphql";
import { InternBrandJobsHealthQuery } from "./__generated__/InternBrandJobsHealthQuery.graphql";
import {
  InternBrandJobsQuery,
  JobStatusEnum,
} from "./__generated__/InternBrandJobsQuery.graphql";
import { InternBrandJobs_jobs$key } from "./__generated__/InternBrandJobs_jobs.graphql";
import InternJobsTimeSeriesChart from "./InternJobsTimeSeriesChart";

const healthQuery = graphql`
  query InternBrandJobsHealthQuery($brandId: String!) {
    internJobHealthMetrics(brandId: $brandId, hours: 24) {
      totalJobs
      successCount
      failureCount
      runningCount
      retryCount
      successRate
      avgDurationMs
      jobsLastHour
      failuresLastHour
    }
    internJobTaskNames(brandId: $brandId) {
      taskName
      count
      successCount
      failureCount
    }
  }
`;

const jobsQuery = graphql`
  query InternBrandJobsQuery(
    $brandId: String!
    $status: JobStatusEnum
    $taskName: String
    $limit: Int!
    $offset: Int!
  ) {
    ...InternBrandJobs_jobs
      @arguments(
        brandId: $brandId
        status: $status
        taskName: $taskName
        limit: $limit
        offset: $offset
      )
  }
`;

const cleanupMutation = graphql`
  mutation InternBrandJobsCleanupMutation($days: Int!) {
    cleanupOldJobs(days: $days) {
      deletedCount
      message
    }
  }
`;

const jobsFragment = graphql`
  fragment InternBrandJobs_jobs on Query
  @refetchable(queryName: "InternBrandJobsRefetchQuery")
  @argumentDefinitions(
    brandId: { type: "String!" }
    status: { type: "JobStatusEnum" }
    taskName: { type: "String" }
    limit: { type: "Int!" }
    offset: { type: "Int!" }
  ) {
    internJobs(
      brandId: $brandId
      status: $status
      taskName: $taskName
      limit: $limit
      offset: $offset
    ) {
      jobs {
        id
        taskId
        taskName
        status
        brandId
        brandName
        startedAt
        completedAt
        durationMs
        inputArgs
        resultData
        errorMessage
        traceback
        retryCount
        worker
        createdAt
      }
      totalCount
      hasNextPage
      hasPreviousPage
    }
  }
`;

// Status color mapping
const STATUS_CONFIG: Record<
  string,
  {
    color: "success" | "error" | "warning" | "info" | "default";
    icon: React.ReactElement;
  }
> = {
  SUCCESS: { color: "success", icon: <CheckCircleIcon fontSize="small" /> },
  FAILURE: { color: "error", icon: <ErrorIcon fontSize="small" /> },
  RUNNING: { color: "info", icon: <CircularProgress size={16} /> },
  PENDING: { color: "default", icon: <ScheduleIcon fontSize="small" /> },
  RETRY: { color: "warning", icon: <RefreshIcon fontSize="small" /> },
};

// Stat card component
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  color?: string;
}

const StatCard = ({ title, value, subtitle, color }: StatCardProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Stack spacing={0.5}>
        <Typography
          variant="body2"
          sx={{
            color: "var(--intern-text-secondary)",
            fontSize: "0.75rem",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: color || "var(--intern-text-primary)",
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

// Format duration helper
const formatDuration = (ms: number | null | undefined): string => {
  if (ms == null) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

// Format date helper
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Extract short task name from full path
const getShortTaskName = (fullName: string): string => {
  const parts = fullName.split(".");
  return parts[parts.length - 1];
};

// Jobs table component
interface JobsTableProps {
  queryRef: InternBrandJobs_jobs$key;
  brandId: string;
  status: JobStatusEnum | null;
  taskName: string | null;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const JobsTable = ({
  queryRef,
  brandId,
  status,
  taskName,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: JobsTableProps) => {
  const [isPending, startTransition] = useTransition();
  const [data, refetch] = useRefetchableFragment(jobsFragment, queryRef);
  const jobs = data.internJobs;

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      startTransition(() => {
        onPageChange(newPage);
        refetch({
          brandId,
          status,
          taskName,
          limit: rowsPerPage,
          offset: newPage * rowsPerPage,
        });
      });
    },
    [brandId, status, taskName, rowsPerPage, onPageChange, refetch]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      startTransition(() => {
        onRowsPerPageChange(newRowsPerPage);
        onPageChange(0);
        refetch({
          brandId,
          status,
          taskName,
          limit: newRowsPerPage,
          offset: 0,
        });
      });
    },
    [brandId, status, taskName, onPageChange, onRowsPerPageChange, refetch]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ opacity: isPending ? 0.6 : 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Started</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.jobs.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
            {jobs.jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    No jobs found for this brand
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={jobs.totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        sx={{
          borderTop: "1px solid var(--intern-border-default)",
        }}
      />
    </Paper>
  );
};

// Individual job row with expandable details
interface JobRowProps {
  job: {
    id: string;
    taskId: string;
    taskName: string;
    status: string;
    brandId: string | null;
    brandName: string | null;
    startedAt: string | null;
    completedAt: string | null;
    durationMs: number | null;
    inputArgs: string | null;
    resultData: string | null;
    errorMessage: string | null;
    traceback: string | null;
    retryCount: number;
    worker: string | null;
    createdAt: string;
  };
}

const JobRow = ({ job }: JobRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.PENDING;

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: "pointer" }}
      >
        <TableCell>
          <Tooltip title={job.taskName} placement="top">
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                maxWidth: 250,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getShortTaskName(job.taskName)}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip
            icon={statusConfig.icon}
            label={job.status}
            color={statusConfig.color}
            size="small"
            sx={{ fontSize: "0.75rem" }}
          />
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}
          >
            {formatDuration(job.durationMs)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {formatDate(job.startedAt || job.createdAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <IconButton size="small">
            <ExpandMoreIcon
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </IconButton>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0 }}>
            <Box sx={{ p: 2, bgcolor: "var(--intern-bg-secondary)" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Task Details
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Task ID:</strong>{" "}
                      <code style={{ fontSize: "0.75rem" }}>{job.taskId}</code>
                    </Typography>
                    <Typography variant="body2">
                      <strong>Full Name:</strong>{" "}
                      <code style={{ fontSize: "0.75rem" }}>
                        {job.taskName}
                      </code>
                    </Typography>
                    <Typography variant="body2">
                      <strong>Worker:</strong> {job.worker || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Retry Count:</strong> {job.retryCount}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Timestamps
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Created:</strong> {formatDate(job.createdAt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Started:</strong> {formatDate(job.startedAt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Completed:</strong> {formatDate(job.completedAt)}
                    </Typography>
                  </Stack>
                </Grid>
                {job.inputArgs && (
                  <Grid item xs={12}>
                    <Accordion
                      elevation={0}
                      sx={{
                        bgcolor: "var(--intern-bg-primary)",
                        border: "1px solid var(--intern-border-default)",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Input Arguments
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          component="pre"
                          sx={{
                            fontSize: "0.75rem",
                            overflow: "auto",
                            maxHeight: 200,
                            bgcolor: "var(--intern-bg-secondary)",
                            p: 1,
                            borderRadius: 1,
                            m: 0,
                          }}
                        >
                          {JSON.stringify(JSON.parse(job.inputArgs), null, 2)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
                {job.resultData && (
                  <Grid item xs={12}>
                    <Accordion
                      elevation={0}
                      sx={{
                        bgcolor: "var(--intern-bg-primary)",
                        border: "1px solid var(--intern-border-default)",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Result Data</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          component="pre"
                          sx={{
                            fontSize: "0.75rem",
                            overflow: "auto",
                            maxHeight: 200,
                            bgcolor: "var(--intern-bg-secondary)",
                            p: 1,
                            borderRadius: 1,
                            m: 0,
                          }}
                        >
                          {JSON.stringify(JSON.parse(job.resultData), null, 2)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
                {job.errorMessage && (
                  <Grid item xs={12}>
                    <Accordion
                      defaultExpanded
                      elevation={0}
                      sx={{
                        bgcolor: "var(--intern-bg-primary)",
                        border: "1px solid #ef4444",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" color="error">
                          Error
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mb: 1 }}
                        >
                          {job.errorMessage}
                        </Typography>
                        {job.traceback && (
                          <Box
                            component="pre"
                            sx={{
                              fontSize: "0.7rem",
                              overflow: "auto",
                              maxHeight: 300,
                              bgcolor: "#1e1e1e",
                              color: "#ef4444",
                              p: 1,
                              borderRadius: 1,
                              m: 0,
                            }}
                          >
                            {job.traceback}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </Grid>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface InternBrandJobsProps {
  brandId: string;
}

const InternBrandJobs = ({ brandId }: InternBrandJobsProps) => {
  const [statusFilter, setStatusFilter] = useState<JobStatusEnum | null>(null);
  const [taskNameFilter, setTaskNameFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [cleanupResult, setCleanupResult] = useState<string | null>(null);

  // Extract the actual brand ID from the Relay global ID (base64 encoded)
  const actualBrandId = (() => {
    try {
      // Relay global IDs are base64 encoded "TypeName:uuid"
      const decoded = atob(brandId);
      return decoded.includes(":") ? decoded.split(":")[1] : brandId;
    } catch {
      // If not base64, try splitting directly
      return brandId.includes(":") ? brandId.split(":")[1] : brandId;
    }
  })();

  const healthData = useLazyLoadQuery<InternBrandJobsHealthQuery>(healthQuery, {
    brandId: actualBrandId,
  });
  const jobsData = useLazyLoadQuery<InternBrandJobsQuery>(jobsQuery, {
    brandId: actualBrandId,
    status: statusFilter,
    taskName: taskNameFilter,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  });

  const [commitCleanup, isCleanupInFlight] =
    useMutation<InternBrandJobsCleanupMutation>(cleanupMutation);

  const metrics = healthData.internJobHealthMetrics;
  const taskNames = healthData.internJobTaskNames;

  const handleCleanup = useCallback(() => {
    if (!window.confirm("Delete all job records older than 30 days?")) return;
    commitCleanup({
      variables: { days: 30 },
      onCompleted: (response) => {
        setCleanupResult(response.cleanupOldJobs.message);
      },
      onError: (error) => {
        setCleanupResult(`Cleanup failed: ${error.message}`);
      },
    });
  }, [commitCleanup]);

  const handleStatusChange = useCallback(
    (event: { target: { value: string } }) => {
      const value = event.target.value;
      setStatusFilter(value === "" ? null : (value as JobStatusEnum));
      setPage(0);
    },
    []
  );

  const handleTaskNameChange = useCallback(
    (event: { target: { value: string } }) => {
      const value = event.target.value;
      setTaskNameFilter(value === "" ? null : value);
      setPage(0);
    },
    []
  );

  if (!metrics) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mb={2}
      >
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={
            isCleanupInFlight ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteSweepIcon />
            )
          }
          disabled={isCleanupInFlight}
          onClick={handleCleanup}
        >
          Cleanup Old Jobs
        </Button>
      </Stack>

      {cleanupResult && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setCleanupResult(null)}
        >
          {cleanupResult}
        </Alert>
      )}

      {/* Health Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2}>
          <StatCard
            title="Total Jobs (24h)"
            value={metrics.totalJobs}
            subtitle={`${metrics.jobsLastHour} last hour`}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            title="Success Rate"
            value={`${metrics.successRate}%`}
            color={metrics.successRate >= 95 ? "#10b981" : "#f59e0b"}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            title="Successful"
            value={metrics.successCount}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            title="Failed"
            value={metrics.failureCount}
            subtitle={`${metrics.failuresLastHour} last hour`}
            color={metrics.failureCount > 0 ? "#ef4444" : undefined}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            title="Running"
            value={metrics.runningCount}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            title="Avg Duration"
            value={formatDuration(metrics.avgDurationMs)}
          />
        </Grid>
      </Grid>

      {/* Time Series Chart */}
      <InternJobsTimeSeriesChart
        brandId={actualBrandId}
        taskNames={taskNames}
      />

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: "var(--intern-bg-primary)",
          border: "1px solid var(--intern-border-default)",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <FilterListIcon sx={{ color: "var(--intern-text-secondary)" }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter || ""}
              label="Status"
              onChange={handleStatusChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="SUCCESS">Success</MenuItem>
              <MenuItem value="FAILURE">Failure</MenuItem>
              <MenuItem value="RUNNING">Running</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="RETRY">Retry</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Task Name</InputLabel>
            <Select
              value={taskNameFilter || ""}
              label="Task Name"
              onChange={handleTaskNameChange}
            >
              <MenuItem value="">All Tasks</MenuItem>
              {taskNames.map((task) => (
                <MenuItem key={task.taskName} value={task.taskName}>
                  {getShortTaskName(task.taskName)} ({task.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(statusFilter || taskNameFilter) && (
            <Button
              size="small"
              onClick={() => {
                setStatusFilter(null);
                setTaskNameFilter(null);
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Jobs Table */}
      <JobsTable
        queryRef={jobsData}
        brandId={actualBrandId}
        status={statusFilter}
        taskName={taskNameFilter}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </Box>
  );
};

export default InternBrandJobs;
